import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBranding } from "@/lib/branding";
import { api } from "@/lib/api";
import {
  MapPin, LayoutDashboard, Trophy, BarChart3, CalendarHeart,
  ArrowRight, ArrowLeft, Sparkles, ShieldCheck, Users, Building2,
  UserCheck, MailCheck, SlidersHorizontal, Plug, Radar, MapPinned, MessagesSquare, Images,
  Cloud, DatabaseBackup, PiggyBank, Clock, Palette, GraduationCap, Brain, HandCoins,
  Smartphone, Fingerprint, WifiOff, Download, PlayCircle, Briefcase,
  Shield, ShieldCheck as ShieldCheckIcon, UserCog, Eye, Lock,
} from "lucide-react";

const FEATURES = [
  {
    id: "agents",
    img: "/learn/class-agents.png",
    icon: Radar,
    tag: "AI + Class Agents",
    title: "AI finds everyone — Class Agents keep them found",
    body: "Built-in AI research scans the public web to locate lost alumni and fill in missing details with cited sources. Designated Class Agents — with scoped, class-only permissions — then verify and steward their year's records.",
    points: ["AI web research with cited sources", "Batch “find everyone missing an email”", "Scoped Class Agent toolkits & emulation"],
  },
  {
    id: "map",
    img: "/learn/map.png",
    icon: MapPin,
    tag: "Directory & Map",
    title: "See where every classmate landed",
    body: "Plot your entire community on an interactive map. Draw a box around any city to instantly pull up who lives there, spot clusters, and target local outreach.",
    points: ["Live geocoding of new imports", "Lasso-select any region", "Click a pin to open a profile"],
  },
  {
    id: "dashboard",
    img: "/learn/dashboard.png",
    icon: LayoutDashboard,
    tag: "Command Center",
    title: "Know your community at a glance",
    body: "A single dashboard surfaces living alumni, contactability, giving momentum and class-by-class breakdowns — sortable by year or size.",
    points: ["Alumni-by-class with smart sorting", "Contactability & email growth", "Quick actions for staff"],
  },
  {
    id: "fundraising",
    img: "/learn/fundraising.png",
    icon: HandCoins,
    tag: "Online Giving",
    title: "Raise more — with giving built right in",
    body: "Launch branded giving campaigns and a beautiful, mobile-friendly donation page in minutes. Alumni choose a fund, pick or enter an amount, and give in seconds — with every gift processed securely through Stripe and reflected against your campaign goal in real time.",
    points: ["Seamless, PCI-compliant Stripe checkout", "Branded campaigns with live progress goals", "Instant receipts & giving history on each record"],
  },
  {
    id: "engagement",
    img: "/learn/engagement.png",
    icon: Trophy,
    tag: "Engagement",
    title: "Celebrate your most involved alumni",
    body: "An engagement leaderboard scores participation across events, giving and updates — so you always know your champions and who to re-engage.",
    points: ["Weighted engagement scoring", "Tiered recognition", "At-risk / dormant flags"],
  },
  {
    id: "reports",
    img: "/learn/reports.png",
    icon: BarChart3,
    tag: "Insights",
    title: "Track growth and opt-outs over time",
    body: "Understand exactly how your reachable list is growing, with opt-out reasons and types charted over time — no spreadsheets required.",
    points: ["Email growth trends", "Opt-out reason breakdowns", "Exportable summaries"],
  },
  {
    id: "communications",
    img: "/learn/communications.png",
    icon: MessagesSquare,
    tag: "Communications",
    title: "Canned templates, fresh sends — every message logged",
    body: "Send from reusable, branded templates or compose something new, preview and safely test before it goes out, and reach exactly the right audience. Every send, resend and backup is captured in a complete activity log.",
    points: ["Reusable “canned” + one-off messages", "Preview & safe test sends", "Full communication activity log"],
  },
  {
    id: "experience",
    img: "/learn/experience.png",
    icon: CalendarHeart,
    tag: "Alumni Experience",
    title: "Reunions, events & chapters in one hub",
    body: "Give alumni a beautiful self-service home for upcoming reunions, past-event galleries, regional chapters and interest groups.",
    points: ["Reunions & RSVP", "Chapters & groups", "Class directories"],
  },
  {
    id: "gallery",
    img: "/learn/gallery.png",
    icon: Images,
    tag: "Event Galleries",
    title: "Blasts from the past — shared by everyone",
    body: "Every reunion and event gets its own photo gallery. Alumni relive the moments and upload their own memories, so the collection grows richer with each gathering — long after the party ends.",
    points: ["Per-event photo galleries", "Alumni upload their own photos", "Relive reunions & milestones together"],
  },
  {
    id: "chapters",
    img: "/learn/chapters.png",
    icon: MapPinned,
    tag: "Chapters & Geography",
    title: "Bring alumni together, wherever they are",
    body: "Organize graduates into regional chapters and interest groups, see them on the map, and empower local leaders — so geography becomes a way to connect rather than a barrier.",
    points: ["Regional chapters by state or metro", "Interest & affinity groups", "Map-driven local outreach"],
  },
  {
    id: "careers",
    img: "/learn/careers.png",
    icon: Briefcase,
    tag: "Business & Career Network",
    title: "Turn your directory into a career network",
    body: "Alumni browse a professional network by employer and industry, opt in to offer or seek mentorship, and post or discover jobs and internships — all with privacy-safe messaging that never exposes an email address.",
    points: ["Employer & industry directory", "Opt-in mentorship — request a mentor privately", "Alumni & staff job board"],
  },
  {
    id: "faculty",
    img: "/learn/faculty.png",
    icon: GraduationCap,
    tag: "Faculty & Staff",
    title: "Invite the professors who taught a class",
    body: "Keep a directory of professors and important staff with the years they served — then enter a class's years and instantly select everyone who was teaching during that era, and invite them all to the reunion in one click.",
    points: ["Invite faculty by the class years they taught", "Auto-selects everyone whose tenure overlaps", "Send tied to an event or as a personal note"],
  },
];

const CAPS = [
  {
    id: "claims",
    icon: UserCheck,
    title: "Alumni claim & maintain their own record",
    body: "Every graduate securely claims their profile from a one-time invitation link, then keeps their contact details, employment and story current going forward — so your directory stays accurate without staff re-keying data.",
    points: ["Secure, one-click profile claim", "Self-service edits anytime", "Every change is logged for staff"],
  },
  {
    id: "comms",
    icon: MailCheck,
    title: "Communications that run themselves",
    body: "Templated, automated messaging handles invitations, reminders and follow-ups with consistent branding and a live preview before send — cutting manual work while giving alumni one coherent, continuous experience.",
    points: ["Reusable branded templates", "Automated invites & reminders", "Recipient exclusions & send preview"],
  },
  {
    id: "optout",
    icon: SlidersHorizontal,
    title: "Opt-out, on their terms",
    body: "A modern preference center lets alumni opt out of everything, or just marketing, or just fundraising / financial appeals — keeping you compliant while preserving the channels that still matter.",
    points: ["Choose: All · Marketing · Financial", "Reasons captured for reporting", "Trends charted over time"],
  },
  {
    id: "integrations",
    icon: Plug,
    title: "Fits the tools you already run",
    body: "Import from a spreadsheet in minutes with smart column-matching, or connect to platforms like Slate, Wild Apricot and Almabase — so your alumni records stay in sync with the systems you already run.",
    points: ["Syncs with Slate, Wild Apricot & Almabase", "Guided CSV / spreadsheet import", "Full backup & restore"],
  },
];

const ROLES = [
  {
    id: "superadmin",
    icon: Shield,
    name: "Super Admin",
    tag: "Full control",
    body: "The account owner. Everything an Administrator can do, plus managing user accounts, branding, backups and the whole configuration of the platform.",
  },
  {
    id: "admin",
    icon: ShieldCheckIcon,
    name: "Administrator",
    tag: "Runs the office",
    body: "Full day-to-day access — the directory, imports, communications, events, giving, reports and backups — to run alumni operations end to end.",
  },
  {
    id: "alumni_manager",
    icon: UserCog,
    name: "Alumni Manager",
    tag: "New role",
    highlight: true,
    body: "A trusted team member who manages alumni, classes, events, giving and the Class Agent toolbox — with Users & Admins, Branding, Backup & Restore and Reports kept off-limits.",
  },
  {
    id: "readonly",
    icon: Eye,
    name: "Read Only",
    tag: "Look, don't touch",
    body: "Search and view the directory, classes and dashboards without the ability to change records — perfect for volunteers or leadership who just need visibility.",
  },
];

const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;
// Live giving-momentum strip for the Online Giving section (public /donations/impact).
const GivingImpactStrip = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/donations/impact").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d || (!d.total_raised && !d.alumni_donors)) return null;
  return (
    <div data-testid="lm-giving-impact" className="mt-6 inline-flex flex-wrap items-stretch gap-px rounded-xl overflow-hidden border border-border bg-border shadow-sm">
      <div className="bg-card px-5 py-3">
        <div className="font-serif text-2xl sm:text-3xl tracking-tight text-primary tabular-nums">{money(d.total_raised)}</div>
        <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-muted-foreground mt-0.5">Raised to date</div>
      </div>
      {d.alumni_donors > 0 && (
        <div className="bg-card px-5 py-3">
          <div className="font-serif text-2xl sm:text-3xl tracking-tight text-primary tabular-nums">{d.alumni_donors.toLocaleString()}</div>
          <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-muted-foreground mt-0.5">Alumni donors</div>
        </div>
      )}
    </div>
  );
};

const Frame = ({ src, alt }) => (
  <div className="rounded-xl overflow-hidden border border-border/70 bg-card shadow-[0_28px_70px_-24px_rgba(0,26,75,0.45)] ring-1 ring-black/5">
    <div className="flex items-center gap-1.5 px-3 py-2 bg-secondary/95 border-b border-black/20">
      <span className="h-2.5 w-2.5 rounded-full bg-white/25" />
      <span className="h-2.5 w-2.5 rounded-full bg-white/25" />
      <span className="h-2.5 w-2.5 rounded-full bg-white/25" />
    </div>
    <img src={src} alt={alt} loading="lazy" className="w-full block bg-muted" />
  </div>
);

export default function LearnMore() {
  const navigate = useNavigate();
  const b = useBranding();
  const [hasVideo, setHasVideo] = useState(false);
  const [videoVer, setVideoVer] = useState(0);
  const videoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/promo-video${videoVer ? `?v=${videoVer}` : ""}`;
  useEffect(() => {
    api.get("/promo-video/available").then((r) => { setHasVideo(!!r.data.available); setVideoVer(r.data.version || 0); }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur bg-background/85 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <button data-testid="lm-home" onClick={() => navigate("/login")} className="flex items-center gap-3">
            {b.logo_primary ? <img src={b.logo_primary} alt="seal" className="h-9 w-9 object-contain" /> : null}
            <span className="font-serif text-lg tracking-tight hidden sm:block">{b.university_name}</span>
          </button>
          <button data-testid="lm-signin-top" onClick={() => navigate("/login")} className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors">
            Sign in <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary to-secondary/95" />
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-accent/20 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6 py-10 sm:py-12 text-white">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-accent">
            <Sparkles className="h-3.5 w-3.5" /> {b.tagline}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-3 max-w-3xl">
            Your whole alumni community, beautifully connected.
          </h1>
          <p className="mt-4 text-white/80 text-base sm:text-lg max-w-2xl leading-relaxed">
            {b.university_name}'s alumni platform brings your directory, map, events, giving and engagement into one modern home — so you can reconnect, steward and celebrate every graduate.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button data-testid="lm-signin-hero" onClick={() => navigate("/login")} className="flex items-center gap-2 px-6 py-3 rounded-md bg-accent text-secondary font-semibold hover:bg-accent/90 transition-colors">
              Sign in to your account <ArrowRight className="h-4 w-4" />
            </button>
            <a href="#features" data-testid="lm-explore" className="flex items-center gap-2 px-6 py-3 rounded-md bg-white/10 text-white font-medium hover:bg-white/20 transition-colors">
              Explore features
            </a>
          </div>
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/70">
            <span className="flex items-center gap-2"><Users className="h-4 w-4 text-accent" /> Self-service alumni profiles</span>
            <span className="flex items-center gap-2"><Building2 className="h-4 w-4 text-accent" /> Chapters & class agents</span>
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-accent" /> Private & secure</span>
          </div>

          {hasVideo && (
            <div data-testid="lm-video-highlights" className="mt-8 max-w-3xl">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-accent mb-2">
                <PlayCircle className="h-3.5 w-3.5" /> Quick Video Highlights
              </div>
              <div className="rounded-xl overflow-hidden border border-white/15 shadow-2xl bg-black ring-1 ring-accent/20">
                <video data-testid="lm-highlights-player" src={videoUrl} poster={b.login_bg || undefined} controls preload="none" playsInline className="w-full h-auto bg-black">
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Ethos */}
      <section className="max-w-6xl mx-auto px-6 pt-10">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: GraduationCap, title: "Built by alumni, for alumni", body: "Designed by people who've been on both sides of the mailing list — around how graduates actually reconnect, show up, and give back." },
            { icon: Palette, title: "Your brand, front and center", body: "Customizable colors, logos and taglines make the platform unmistakably yours — every institution looks and feels like its own." },
            { icon: Brain, title: "Powered by the latest AI", body: "Leverages breakthroughs in AI that simply weren't possible a short time ago — to locate, enrich and understand your community." },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.title} data-testid={`lm-ethos-${c.title.split(" ")[0].toLowerCase()}`} className="rounded-xl bg-card border border-border p-6 shadow-[0_2px_10px_rgba(0,26,75,0.05)]">
                <div className="h-11 w-11 rounded-lg bg-accent/15 text-primary flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                <h3 className="font-serif text-xl tracking-tight mt-4">{c.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Feature showcase */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-14 space-y-16">
        {FEATURES.map((f, i) => {
          const Icon = f.icon;
          const flip = i % 2 === 1;
          return (
            <div key={f.id} data-testid={`lm-feature-${f.id}`} className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className={flip ? "lg:order-2" : ""}>
                <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-primary">
                  <span className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center"><Icon className="h-4 w-4" /></span>
                  {f.tag}
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-4">{f.title}</h2>
                <p className="mt-4 text-muted-foreground leading-relaxed">{f.body}</p>
                <ul className="mt-6 space-y-2.5">
                  {f.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-sm">
                      <span className="h-1.5 w-1.5 rounded-full bg-accent" /> {p}
                    </li>
                  ))}
                </ul>
                {f.id === "fundraising" && <GivingImpactStrip />}
              </div>
              <div className={flip ? "lg:order-1" : ""}>
                <Frame src={f.img} alt={f.title} />
              </div>
            </div>
          );
        })}
      </section>

      {/* Mobile app */}
      <section className="relative bg-secondary text-white overflow-hidden">
        <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-accent/20 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Phone mockup */}
          <div className="flex justify-center lg:justify-start order-2 lg:order-1" data-testid="lm-mobile-mockup">
            <div className="relative w-[264px] shrink-0">
              <div className="rounded-[2.6rem] bg-black p-2.5 shadow-[0_40px_90px_-20px_rgba(0,0,0,0.6)] ring-1 ring-white/10">
                <div className="absolute left-1/2 -translate-x-1/2 top-3.5 h-5 w-28 rounded-full bg-black z-10" />
                <div className="rounded-[2.1rem] overflow-hidden bg-white">
                  <img src="/learn/mobile-experience.png" alt="The alumni class experience on a phone" loading="lazy" className="w-full block" />
                </div>
              </div>
            </div>
          </div>
          {/* Copy */}
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-accent">
              <Smartphone className="h-3.5 w-3.5" /> Mobile App
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-4">The class experience, right in their pocket</h2>
            <p className="mt-4 text-white/75 leading-relaxed">
              There's nothing to download from an app store. The platform installs straight to any phone's home screen as a fast, full-screen app — so alumni open it with a tap and instantly land in their class experience: reunions, events, classmates and photo galleries, beautifully tuned for mobile.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                { icon: Download, t: "Add to Home Screen — no app store needed", d: "Installs in seconds on iPhone and Android; opens full-screen like a native app." },
                { icon: Fingerprint, t: "Sign in with Face ID or fingerprint", d: "Secure passkey sign-in means no passwords to remember on the go." },
                { icon: WifiOff, t: "Built for real phones", d: "Responsive, touch-first layouts stay quick and readable on any screen." },
              ].map((p) => {
                const Icon = p.icon;
                return (
                  <li key={p.t} className="flex items-start gap-3">
                    <span className="mt-0.5 h-9 w-9 shrink-0 rounded-lg bg-accent/20 text-accent flex items-center justify-center"><Icon className="h-4 w-4" /></span>
                    <div>
                      <div className="text-sm font-semibold">{p.t}</div>
                      <div className="text-sm text-white/65 leading-relaxed">{p.d}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="relative bg-secondary text-white overflow-hidden">
        <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-primary/40 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6 py-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-accent">
              <Sparkles className="h-3.5 w-3.5" /> Why teams choose us
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-4">Less busywork. Better continuity.</h2>
            <p className="mt-4 text-white/75 leading-relaxed">
              The platform is built so alumni help keep their own data current and routine outreach runs on autopilot — freeing your team to focus on relationships, not spreadsheets.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            {CAPS.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.id} data-testid={`lm-cap-${c.id}`} className="rounded-xl bg-white/[0.06] border border-white/10 p-6 hover:bg-white/[0.09] transition-colors">
                  <div className="h-11 w-11 rounded-lg bg-accent/20 text-accent flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                  <h3 className="font-serif text-xl tracking-tight mt-4">{c.title}</h3>
                  <p className="mt-2.5 text-sm text-white/70 leading-relaxed">{c.body}</p>
                  <ul className="mt-4 space-y-2">
                    {c.points.map((p) => (
                      <li key={p} className="flex items-center gap-2.5 text-sm text-white/85">
                        <span className="h-1.5 w-1.5 rounded-full bg-accent shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Roles & access */}
      <section className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-primary">
              <Lock className="h-3.5 w-3.5" /> Roles & access
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-4">Give each teammate exactly the right access</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Role-based permissions keep sensitive settings locked down while letting the people who steward your community get real work done — including our newest role, the <span className="font-semibold text-foreground">Alumni Manager</span>.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
            {ROLES.map((r) => {
              const Icon = r.icon;
              return (
                <div
                  key={r.id}
                  data-testid={`lm-role-${r.id}`}
                  className={`relative rounded-xl border p-6 shadow-[0_2px_10px_rgba(0,26,75,0.05)] transition-colors ${
                    r.highlight ? "bg-primary/[0.04] border-primary/40 ring-1 ring-primary/20" : "bg-card border-border"
                  }`}
                >
                  {r.highlight && (
                    <span className="absolute top-4 right-4 text-[10px] font-mono uppercase tracking-[0.15em] bg-accent text-secondary rounded-full px-2 py-0.5">
                      New
                    </span>
                  )}
                  <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${r.highlight ? "bg-primary/15 text-primary" : "bg-accent/15 text-primary"}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-muted-foreground mt-4">{r.tag}</div>
                  <h3 className="font-serif text-xl tracking-tight mt-1">{r.name}</h3>
                  <p className="mt-2.5 text-sm text-muted-foreground leading-relaxed">{r.body}</p>
                </div>
              );
            })}
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            Alumni also get their own secure self-service experience, and <span className="text-foreground font-medium">Class Agents</span> receive a scoped toolkit for just their graduating class.
          </p>
        </div>
      </section>

      {/* Value / cost */}
      <section className="border-t border-border bg-gradient-to-b from-accent/[0.07] to-transparent">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-primary">
              <Cloud className="h-3.5 w-3.5" /> Cloud-native &amp; cost-smart
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-4">Half the cost. Four times the results.</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Built and run entirely in the cloud, the platform backs itself up automatically and needs almost no day-to-day upkeep — so a lean team gets far more done. Most schools spend roughly half of what legacy advancement systems cost, with several times the impact.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
            {[
              { icon: Cloud, title: "Runs in the cloud", body: "Always on and always current — no servers to buy, patch or babysit." },
              { icon: DatabaseBackup, title: "Backs itself up", body: "Automatic, scheduled backups keep your alumni data safe with zero effort." },
              { icon: PiggyBank, title: "~50% lower cost", body: "Typically about half the price of the legacy systems it replaces." },
              { icon: Clock, title: "Minutes, not months", body: "Import, research and message in minutes — a fraction of the admin time." },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} data-testid={`lm-value-${c.title.split(" ")[0].toLowerCase()}`} className="rounded-xl bg-card border border-border p-5 shadow-[0_2px_10px_rgba(0,26,75,0.05)]">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                  <h3 className="font-serif text-lg tracking-tight mt-3">{c.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{c.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-border bg-muted/30">
        <div className="max-w-4xl mx-auto px-6 py-12 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">Ready to reconnect?</h2>
          <p className="mt-3 text-muted-foreground">Sign in with the email your alumni office has on file, or claim your profile from an invitation link.</p>
          <button data-testid="lm-signin-bottom" onClick={() => navigate("/login")} className="mt-7 inline-flex items-center gap-2 px-7 py-3 rounded-md bg-primary text-white font-semibold hover:bg-primary/90 transition-colors">
            Go to sign in <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between text-xs text-muted-foreground">
          <button onClick={() => navigate("/login")} className="flex items-center gap-1.5 hover:text-foreground transition-colors"><ArrowLeft className="h-3.5 w-3.5" /> Back to sign in</button>
          <span>© {new Date().getFullYear()} {b.university_name} · Office of Alumni Relations</span>
        </div>
      </footer>
    </div>
  );
}
